import React from "react";
import { reportar } from "@/lib/erroresFront";

/**
 * La red de seguridad de la app (#116, 15-oct-2026): un error de JavaScript en el render ya no deja la pantalla en
 * blanco. Atrapa el error, lo registra en el servidor (`lib/erroresFront.js` → `POST /api/front/errores`) y muestra un
 * cartel con «Recargar» e «Ir al inicio».
 *
 * Hay dos: `alcance="pantalla"` envuelve lo que cambia con la ruta (el `Outlet` de `AppLayout`, con `key` por ruta: al
 * navegar a otra pantalla se limpia solo) y deja el menú y el encabezado vivos; `alcance="app"` envuelve todo desde
 * `index.js`, para lo que falla fuera del panel (el ingreso, la app del chofer, los proveedores). Es una clase porque
 * React no tiene otra forma de atrapar un error de render; no usa hooks ni contextos a propósito: tiene que funcionar
 * aunque lo que se rompió sea un proveedor.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error: error || new Error("Error sin detalle") };
  }

  componentDidCatch(error, info) {
    try {
      reportar(error, info, { origen: "render", pantalla: this.props.pantalla || null });
    } catch { /* registrar nunca puede romper el cartel */ }
  }

  componentDidUpdate(prevProps) {
    // Con `key` por ruta el boundary se remonta solo; esto cubre a quien lo usa sin key y cambia `reiniciarCon`.
    if (this.state.error && prevProps.reiniciarCon !== this.props.reiniciarCon) {
      this.setState({ error: null });
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const app = this.props.alcance === "app";
    const recargar = () => { try { window.location.reload(); } catch { /* nada */ } };
    const alInicio = () => { try { window.location.assign("/"); } catch { /* nada */ } };
    return (
      <div className={`flex flex-col items-center justify-center gap-3 p-6 text-center ${app ? "min-h-screen bg-qb-bg" : "flex-1"}`}
           data-testid={`error-boundary-${app ? "app" : "pantalla"}`} role="alert">
        <div className="text-[17px] font-bold text-qb-text">{app ? "Algo falló en la app" : "Algo falló en esta pantalla"}</div>
        <div className="text-[13px] text-qb-muted max-w-[420px] leading-relaxed">
          Ya quedó registrado para que lo revisemos. Probá recargar{app ? "" : " la pantalla"}; si vuelve a pasar, avisanos por
          Soporte diciendo qué estabas haciendo.
        </div>
        <div className="text-[11.5px] text-qb-muted/80 max-w-[520px] break-words" data-testid="error-boundary-detalle">
          {String(error?.message || error).slice(0, 200)}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <button type="button" onClick={recargar} data-testid="error-boundary-recargar"
                  className="px-3 py-1.5 rounded-[6px] bg-qb-primary text-white text-[13px] font-medium hover:opacity-90">
            Recargar
          </button>
          {!app && (
            <button type="button" onClick={alInicio} data-testid="error-boundary-inicio"
                    className="px-3 py-1.5 rounded-[6px] border-hair border-qb-border bg-white text-[13px] text-qb-text hover:bg-qb-bg">
              Ir al inicio
            </button>
          )}
        </div>
      </div>
    );
  }
}
